import { useEffect, useRef, useState, type ReactNode } from "react";

/* Scales its content to fit the available box (contain), centered. Used for
   widgets that don't reflow on their own (e.g. the pipeline chevrons, KPI
   numbers) so they grow/shrink with a resizable card instead of clipping.

   - `designWidth` gives the content a fixed natural width to scale from (needed
     for full-width widgets like the pipeline); omit for intrinsically-sized
     content.
   - `maxScale` caps zoom so text/graphics don't blur when a card is huge.

   Transforms don't change offsetWidth/Height, so observing the content never
   feeds back into the scale (no resize loop). */
export function FitBox({
  children,
  designWidth,
  maxScale = 2,
  className = "",
}: {
  children: ReactNode;
  designWidth?: number;
  maxScale?: number;
  className?: string;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const box = boxRef.current;
    const content = contentRef.current;
    if (!box || !content) return;

    const measure = () => {
      const bw = box.clientWidth;
      const bh = box.clientHeight;
      const cw = content.offsetWidth || 1;
      const ch = content.offsetHeight || 1;
      if (!bw || !bh) return;
      const s = Math.min(bw / cw, bh / ch);
      setScale(Math.max(0.1, Math.min(s, maxScale)));
    };

    const ro = new ResizeObserver(measure);
    ro.observe(box);
    ro.observe(content);
    measure();
    return () => ro.disconnect();
  }, [maxScale]);

  return (
    <div ref={boxRef} className={`relative h-full w-full overflow-hidden ${className}`}>
      <div
        ref={contentRef}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: designWidth,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: "center center",
        }}
      >
        {children}
      </div>
    </div>
  );
}
